import React, { useEffect, useState, useMemo } from 'react';
import { useTable, usePagination } from 'react-table';
import ReactPaginate from 'react-paginate';
import './Dashboard.scss';
import DetailsPopup from '../../Components/DetailsPopup/DetailsPopup';
import DashboardFilter from '../../Components/DashboardFilter/DashboardFilter';

const Dashboard = () => {
  const [data, setData] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [currentDetails, setCurrentDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 12;

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('publishingDate');
  const [orderOption, setOrderOption] = useState('descending');
  const [ratingSortDirection, setRatingSortDirection] = useState(null);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/wp-json/feedback/v1/get');
        let fetchedData = await response.json();

        if (!Array.isArray(fetchedData)) {
          console.error('Data fetched is not an array:', fetchedData);
          setData([]);
          return;
        }

        if (searchTerm) {
          fetchedData = fetchedData.filter(item =>
            item.post_title.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        fetchedData.sort((a, b) => {
          let valueA, valueB;

          switch (sortOption) {
            case 'postTitle':
              valueA = a.post_title.toLowerCase();
              valueB = b.post_title.toLowerCase();
              break;
            case 'publishingDate':
              valueA = new Date(a.post_date);
              valueB = new Date(b.post_date);
              break;
            case 'rating':
              const totalFeedbackA = parseInt(a.positive_feedback) + parseInt(a.negative_feedback) || 0;
              const totalFeedbackB = parseInt(b.positive_feedback) + parseInt(b.negative_feedback) || 0;
              const ratingPercentageA = totalFeedbackA > 0 ? (parseInt(a.positive_feedback) / totalFeedbackA) * 100 : -1;
              const ratingPercentageB = totalFeedbackB > 0 ? (parseInt(b.positive_feedback) / totalFeedbackB) * 100 : -1;

              if (ratingSortDirection === 'positive') {
                if (ratingPercentageA === -1 && ratingPercentageB !== -1) return 1;
                if (ratingPercentageB === -1 && ratingPercentageA !== -1) return -1;
                return ratingPercentageB - ratingPercentageA; // Descending for positive
              } else if (ratingSortDirection === 'negative') {
                if (ratingPercentageA === -1 && ratingPercentageB !== -1) return 1;
                if (ratingPercentageB === -1 && ratingPercentageA !== -1) return -1;
                return ratingPercentageA - ratingPercentageB;
              }
              break;
            default:
              valueA = a.post_title.toLowerCase();
              valueB = b.post_title.toLowerCase();
          }

          if (orderOption === 'ascending') {
            return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
          } else {
            return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
          }
        });




        const processedData = fetchedData.map(item => ({
          postId: item.ID,
          postTitle: item.post_title,
          postUrl: `/${item.slug}`,
          publishingDate: new Date(item.post_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }),
          selectedOption: item.selected_option || 'No feedback provided',
          rating: {
            positiveCount: parseInt(item.positive_feedback) || 0,
            negativeCount: parseInt(item.negative_feedback) || 0,
            ratingPercentage: ((parseInt(item.positive_feedback) || 0) + (parseInt(item.negative_feedback) || 0)) > 0 ?
              Math.round(((parseInt(item.positive_feedback) || 0) / ((parseInt(item.positive_feedback) || 0) + (parseInt(item.negative_feedback) || 0))) * 100) : 0
          }
        }));

        setData(processedData);

      } catch (error) {
        console.error('Failed to fetch data:', error);
        setData([]);
      }
    };

    fetchData();
  }, [searchTerm, sortOption, orderOption, ratingSortDirection, currentPage]);


  const fetchDetails = (postId) => {
    fetch(`/wp-json/feedback/v1/get-details/${postId}`)
      .then(response => response.json())
      .then(details => {
        if (details && details.length > 0) {
          setCurrentDetails({
            details: details,
            postTitle: details[0].post_title,
            itemCount: details.length
          });
          setPopupOpen(true);
        } else {

          setCurrentDetails({
            details: [],
            postTitle: 'No details available',
            itemCount: 0
          });
          setPopupOpen(false);
        }
      })
      .catch(error => {
        console.error('Failed to fetch details:', error);

        setCurrentDetails({
          details: [],
          postTitle: 'Failed to fetch details',
          itemCount: 0
        });
        setPopupOpen(false);
      });
  };


  const columns = useMemo(() => [
    {
      Header: 'Post Title',
      accessor: 'postTitle',
      Cell: ({ row }) => (
        <a className="post__title" href={row.original.postUrl} target="_blank" rel="noopener noreferrer">
          {row.values.postTitle}
        </a>
      )
    },
    { Header: 'Publishing Date', accessor: 'publishingDate' },
    {
      Header: 'Rating',
      accessor: 'rating',
      Cell: ({ value }) => {
        if (value && (value.positiveCount > 0 || value.negativeCount > 0)) {
          const { positiveCount, negativeCount, ratingPercentage } = value;
          const color = ratingPercentage >= 50 ? 'green' : 'red';
          return (
            <span>
              <span className={`rating-indicator ${color}`}></span>
              {`${ratingPercentage}% (${positiveCount}/${negativeCount})`}
            </span>
          );
        } else {
          return (
            <span>
              <span className="rating-indicator gray"></span>
              N/A (0/0)
            </span>
          );
        }
      }
    },
    {
      Header: '',
      id: 'view',
      accessor: (str) => 'view',
      Cell: ({ row }) => (
        <button onClick={() => fetchDetails(row.original.postId)}
          className={row.original.rating.positiveCount === 0 && row.original.rating.negativeCount === 0 ? 'disabled-button' : ''}>
          View Data
        </button>
      )
    }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage
  } = useTable({
    columns,
    data,
    initialState: { pageIndex: currentPage, pageSize: itemsPerPage }
  }, usePagination);

  const handlePageClick = data => {
    const newPage = data.selected;
    setCurrentPage(newPage);
    gotoPage(newPage);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSortChange = (option) => {
    setSortOption(option);
  };

  const handleOrderChange = (option) => {
    setOrderOption(option);
  };

  const handleRatingSortDirectionChange = (direction) => {
    setRatingSortDirection(direction);
    setSortOption('rating');
  };



  return (
    <div className='dashboard'>

      {popupOpen && <div className="dashboard__backdrop" />}
      <DetailsPopup
        isOpen={popupOpen}
        details={currentDetails?.details}
        postTitle={currentDetails?.postTitle}
        itemCount={currentDetails?.itemCount}
        onClose={() => setPopupOpen(false)}
      />


      <div className="dashboard__content">
        <ReactPaginate
          previousLabel="previous"
          nextLabel="next"
          breakLabel="..."
          pageCount={pageCount}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          forcePage={currentPage}
          renderOnZeroPageCount={null}
        />
        <table className="dashboard__table" {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}><strong>{column.render('Header')}</strong></th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        <ReactPaginate
          previousLabel="previous"
          nextLabel="next"
          breakLabel="..."
          pageCount={pageCount}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          forcePage={currentPage}
          renderOnZeroPageCount={null}
        />
      </div>

      <div className="dashboard__filter">
        <DashboardFilter
          onSearch={handleSearch}
          onSortChange={handleSortChange}
          onOrderChange={handleOrderChange}
          onRatingSortDirectionChange={handleRatingSortDirectionChange}
        />
      </div>
    </div>
  );
}

export default Dashboard;